import { useEffect, useMemo, useState } from 'react'
import type { EquivalentDomainGroupWire } from '@features/equivalent-domains/api/equivalentDomainsApi'
import { useEquivalentDomains } from '@features/equivalent-domains/hooks/useEquivalentDomains'
import { preferencesStore } from '@features/preferences/services/preferencesStore'
import { buildVaultListEntries, type VaultListEntry } from '../services/vaultDomainGroups'
import type { VaultItem } from '../types'

/**
 * Vault 一覧アイテムを「ドメインルール」グループ統合済みの表示行へ変換する。
 *
 * - `#settings/domain` で登録した equivalent-domain group を取得する。
 * - 環境設定の「同等ドメインを統合表示」(`mergeDomains`) が ON のときだけ統合する。
 * - group 取得に失敗 / loading 中は素直に全件 `item` 行を返す (一覧を壊さない)。
 */

// useMemo の依存安定化用: ready でないとき groups の参照を固定する。
const EMPTY_GROUPS: readonly EquivalentDomainGroupWire[] = []

export const useVaultListEntries = (items: readonly VaultItem[]): VaultListEntry[] => {
  const { state } = useEquivalentDomains()

  const [mergeDomains, setMergeDomains] = useState<boolean>(
    () => preferencesStore.getResolved().mergeDomains,
  )

  useEffect(() => {
    return preferencesStore.subscribe(prefs => {
      setMergeDomains(prefs.mergeDomains)
    })
  }, [])

  const groups = state.kind === 'ready' ? state.groups : EMPTY_GROUPS

  return useMemo(
    () => buildVaultListEntries(items, groups, mergeDomains),
    [items, groups, mergeDomains],
  )
}
